import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import type { CallbackDataParams } from 'echarts/types/dist/shared'
import { Lock } from 'lucide-react'
import type { WireArticle } from '../../api/types'
import type { CanvasSlot } from '../editor-shared/canvasTypes'
import { COL_KEYS } from '../editor-shared/canvasTypes'
import { PAGE_BLOCK_REGISTRY } from '../page-blocks/registry'
import type { PageArticleInlineMetrics } from './PageInlineMetricsCard'

type PageMetricsSunburstProps = {
  slots: CanvasSlot[]
  slotArticleMap: Record<string, WireArticle[]>
  pageMetricsByArticleId: Record<string, PageArticleInlineMetrics>
  totalPageViewsFromPage: number
  onHoverChange?: (target: { blockId: string | null; articleId: string | null } | null) => void
  onLockToggle?: (target: { blockId: string | null; articleId: string | null }) => void
  lockedTarget?: { blockId: string | null; articleId: string | null } | null
}

type SunburstNode = {
  name: string
  value: number
  children?: SunburstNode[]
  itemStyle?: {
    color?: string
    opacity?: number
    borderWidth?: number
    borderColor?: string
  }
  nodeType?: 'block' | 'article'
  articleCount?: number
  blockId?: string
  articleId?: string
}

const BLOCK_COLORS = ['#0f766e', '#2563eb', '#c2410c', '#7c3aed', '#b91c1c', '#0891b2', '#4f46e5', '#15803d']

function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '')
  const bigint = Number.parseInt(normalized, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function articleDisplayTitle(article: WireArticle): string {
  return article.workflow?.stage === 'recalled' ? 'This Article Has Been Recalled' : article.title
}

function slotLabel(slot: CanvasSlot): string {
  const sectionTitle = typeof slot.config.sectionHeaderTitle === 'string' ? slot.config.sectionHeaderTitle.trim() : ''
  if (sectionTitle) return sectionTitle
  return PAGE_BLOCK_REGISTRY[slot.type]?.label ?? slot.type
}

function resolveChartColors() {
  if (typeof window === 'undefined') {
    return {
      fg: '#e5e7eb',
      fgSecondary: '#9ca3af',
      line: '#374151',
      tooltipBg: '#111827',
      tooltipFg: '#f9fafb',
    }
  }
  const styles = getComputedStyle(document.documentElement)
  const get = (variable: string) => styles.getPropertyValue(variable).trim()
  return {
    fg: get('--gray-12') || '#111111',
    fgSecondary: get('--gray-11') || '#6b7280',
    line: get('--gray-6') || '#e5e7eb',
    tooltipBg: get('--gray-12') || '#111111',
    tooltipFg: get('--gray-1') || '#fcfcfc',
  }
}

function buildNodesForSlot(
  slot: CanvasSlot,
  slotArticleMap: Record<string, WireArticle[]>,
  pageMetricsByArticleId: Record<string, PageArticleInlineMetrics>,
  colorIndexRef: { current: number },
): SunburstNode[] {
  if (slot.type === 'editorial-grid') {
    const nodes: SunburstNode[] = []
    for (const colKey of COL_KEYS) {
      for (const colBlock of slot.columnBlocks?.[colKey] ?? []) {
        const colPrefix = `${colKey}:`
        const colConfig: Record<string, string | number | boolean | null> = {
          ...PAGE_BLOCK_REGISTRY[colBlock.type].defaultConfig,
          ...Object.fromEntries(
            Object.entries(slot.config)
              .filter(([key]) => key.startsWith(colPrefix))
              .map(([key, value]) => [key.slice(colPrefix.length), value as string | number | boolean | null]),
          ),
        }
        const childSlot: CanvasSlot = {
          id: `${slot.id}:${colKey}:${colBlock.id}`,
          type: colBlock.type,
          order: 0,
          config: colConfig,
        }
        nodes.push(...buildNodesForSlot(childSlot, slotArticleMap, pageMetricsByArticleId, colorIndexRef))
      }
    }
    return nodes
  }

  const articles = slotArticleMap[slot.id] ?? []
  const articleNodes = articles
    .map(article => ({
      name: articleDisplayTitle(article),
      value: pageMetricsByArticleId[article.id]?.viewsFromPage ?? 0,
      articleId: article.id,
    }))
    .filter(article => article.value > 0)

  const total = articleNodes.reduce((sum, article) => sum + article.value, 0)
  if (total === 0) return []

  const baseColor = BLOCK_COLORS[colorIndexRef.current % BLOCK_COLORS.length]
  colorIndexRef.current += 1

  return [{
    name: slotLabel(slot),
    value: total,
    nodeType: 'block',
    blockId: slot.id,
    articleCount: articleNodes.length,
    itemStyle: { color: baseColor },
    children: articleNodes.map((article, index) => ({
      name: article.name,
      value: article.value,
      nodeType: 'article',
      blockId: slot.id,
      articleId: article.articleId,
      itemStyle: { color: hexToRgba(baseColor, Math.max(0.38, 0.82 - index * 0.1)) },
    })),
  }]
}

function applyLockStyles(
  nodes: SunburstNode[],
  lockedTarget: { blockId: string | null; articleId: string | null },
  lineColor: string,
): SunburstNode[] {
  const isMatch = (node: SunburstNode): boolean => {
    if (!lockedTarget.blockId || !node.blockId) return false
    if (node.blockId !== lockedTarget.blockId) return false
    if (!lockedTarget.articleId) return true
    return node.nodeType === 'block' || node.articleId === lockedTarget.articleId
  }

  return nodes.map(node => {
    const matched = isMatch(node)
    return {
      ...node,
      itemStyle: {
        ...(node.itemStyle ?? {}),
        opacity: matched ? 1 : 0.18,
        borderWidth: matched ? 3 : 1,
        borderColor: matched ? lineColor : (node.itemStyle?.borderColor ?? lineColor),
      },
      children: node.children ? applyLockStyles(node.children, lockedTarget, lineColor) : undefined,
    }
  })
}

export function PageMetricsSunburst({
  slots,
  slotArticleMap,
  pageMetricsByArticleId,
  totalPageViewsFromPage,
  onHoverChange,
  onLockToggle,
  lockedTarget = null,
}: PageMetricsSunburstProps) {
  const colors = resolveChartColors()

  const nodes = useMemo(() => {
    const colorIndexRef = { current: 0 }
    return slots.flatMap(slot => buildNodesForSlot(slot, slotArticleMap, pageMetricsByArticleId, colorIndexRef))
  }, [pageMetricsByArticleId, slotArticleMap, slots])

  const displayNodes = useMemo(
    () => (lockedTarget?.blockId ? applyLockStyles(nodes, lockedTarget, colors.line) : nodes),
    [colors.line, lockedTarget, nodes],
  )

  const option = useMemo<EChartsOption>(() => ({
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      backgroundColor: colors.tooltipBg,
      borderColor: colors.line,
      textStyle: { color: colors.tooltipFg, fontSize: 11 },
      formatter: ((params: CallbackDataParams | CallbackDataParams[]) => {
        const entry = Array.isArray(params) ? params[0] : params
        if (!entry) return ''
        const data = (entry.data ?? {}) as SunburstNode
        const value = data.value ?? 0
        const pct = totalPageViewsFromPage > 0 ? ((value / totalPageViewsFromPage) * 100).toFixed(1) : '0.0'
        if (data.nodeType === 'article') {
          return `<b>${data.name}</b><br/>Views from page: <b>${value.toLocaleString()}</b><br/>Share of page: <b>${pct}%</b>`
        }
        return `<b>${data.name}</b><br/>Views from page: <b>${value.toLocaleString()}</b><br/>Share of page: <b>${pct}%</b><br/>Articles: <b>${data.articleCount ?? 0}</b>`
      }) as unknown as string,
    },
    series: [{
      type: 'sunburst',
      radius: ['18%', '92%'],
      sort: undefined,
      nodeClick: false,
      emphasis: { focus: 'ancestor' },
      data: displayNodes,
      itemStyle: {
        borderWidth: 2,
        borderColor: colors.line,
      },
      label: {
        color: colors.fg,
        rotate: 'radial',
        fontSize: 10,
      },
      levels: [
        {},
        {
          r0: '18%',
          r: '52%',
          label: { rotate: 'tangential', fontSize: 11, minAngle: 8 },
        },
        {
          r0: '54%',
          r: '92%',
          label: { show: false },
        },
      ],
    }],
  }), [colors.fg, colors.line, colors.tooltipBg, colors.tooltipFg, displayNodes, totalPageViewsFromPage])

  if (nodes.length === 0 || totalPageViewsFromPage === 0) return null

  const onEvents = {
    mouseover: (params: CallbackDataParams) => {
      const data = (params.data ?? {}) as SunburstNode
      if (!data.blockId) return
      onHoverChange?.({
        blockId: data.blockId,
        articleId: data.articleId ?? null,
      })
    },
    mouseout: () => {
      onHoverChange?.(null)
    },
    globalout: () => {
      onHoverChange?.(null)
    },
    click: (params: CallbackDataParams) => {
      const data = (params.data ?? {}) as SunburstNode
      if (!data.blockId) return
      onLockToggle?.({
        blockId: data.blockId,
        articleId: data.articleId ?? null,
      })
    },
  }

  return (
    <section className="rounded-xl border border-line bg-surface p-4">
      <div className="mb-3 flex items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Traffic Composition</p>
          <div className="mt-1 flex items-center gap-2">
            <h2 className="text-sm font-semibold text-fg">How This Page Distributes Attention</h2>
            {lockedTarget?.blockId && (
              <span className="inline-flex items-center gap-1 rounded-full border border-line bg-accent-primary-fill px-2 py-0.5 text-[10px] font-semibold text-accent-primary-fg">
                <Lock size={10} />
                Locked
              </span>
            )}
          </div>
        </div>
        <p className="text-xs text-fg-secondary">
          {lockedTarget?.blockId ? 'Click locked slice again to unlock' : 'Hover blocks and article slices. Click to lock and unlock.'}
        </p>
      </div>
      <div className="relative">
        <ReactECharts option={option} style={{ height: 430 }} onEvents={onEvents} />
        <div className="pointer-events-none absolute left-1/2 top-1/2 flex h-28 w-28 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border border-line bg-canvas/90 text-center shadow-sm backdrop-blur-sm">
          <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">From This Page</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-fg">{totalPageViewsFromPage.toLocaleString()}</p>
          <p className="mt-1 text-[10px] text-fg-secondary">article views</p>
        </div>
      </div>
    </section>
  )
}